import { ProductListAnalyticsService } from '@analytics/feature';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CustomActiveCartService } from '@cart/data-access';
import { ComparisonService } from '@comparison/feature';
import { FeatureFlagService } from '@core/feature';
import { BonusTypes, CustomProduct, Price, ProductBonus, ProductGridItem } from '@product/const';
import { PriceService, ProductBonusService, SelectedPriceService } from '@product/data-access';
import { TransitPriceMode, TransitPriceStateService } from '@product/feature';
import { ProductPipesModule } from '@product/utils';
import { RetailRocketCarouselStateService } from '@reference/feature';
import { ProductListReviewsService } from '@reviews/data-access';
import { BonusWidgetComponent, SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { OrderEntry, UrlModule } from '@spartacus/core';
import { ProductListItemContext, ProductListItemContextSource } from '@spartacus/storefront';
import { BonusSystemFeatureAvailableService } from '@user/feature';
import { ProductWishlistService } from '@wishlist/feature';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  ProductButtonComponent,
  ProductButtonMobileComponent,
  SdvorTransitOrderRequestComponent,
} from '../product-button';
import { SdProductListAvailabilityComponent } from '../product-list-availability';
import { SdvorProductListImageComponent } from '../product-list-image';
import { SdvorProductItemUpperButtonsComponent } from './product-grid-item-upper-buttons';
import { ProductPriceAvailableComponent, ProductPriceTransitComponent } from './product-price';
import { UnitTabComponent } from './unit-tab';

@Component({
  selector: 'sd-product-item',
  templateUrl: './product-item.component.html',
  styleUrls: ['./product-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterLink,
    UrlModule,
    SdvorProductListImageComponent,
    SdvorIconComponent,
    BonusWidgetComponent,
    SdvorTransitOrderRequestComponent,
    UnitTabComponent,
    ProductPipesModule,
    ProductButtonComponent,
    ProductButtonMobileComponent,
    SdvorProductItemUpperButtonsComponent,
    SdProductListAvailabilityComponent,
    ProductPriceAvailableComponent,
    ProductPriceTransitComponent,
  ],
  providers: [
    ProductListItemContextSource,
    {
      provide: ProductListItemContext,
      useExisting: ProductListItemContextSource,
    },
  ],
})
export class SdvorProductItemComponent implements OnInit, OnChanges {
  @Input() product: ProductGridItem;
  @Input() withAccent = false;
  // порядковый номер товара в списке (для аналитики)
  @Input() productIndex?: number;
  @Input() hideWishlist = false;

  BonusTypes = BonusTypes;
  hasEntry$: Observable<boolean>;
  prices$: Observable<Price[]>;
  hasPersonalPrice$: Observable<boolean>;
  bonuses$: Observable<ProductBonus | null>;
  transitInstance$: Observable<TransitPriceMode | undefined>;
  rating$: Observable<string>;
  isFeatureAvailable$: Observable<boolean>;
  isOutOfStockWithPriceFlagEnabled$: Observable<boolean>;
  desktopWishlistVisible$: Observable<boolean>;
  desktopComparisonVisible$: Observable<boolean>;
  isDesktop = this.deviceService.isDesktopLgUp$;
  isTabletUp$ = this.deviceService.isTabletMdUp$;

  constructor(
    @Optional() private productListAnalyticsService: ProductListAnalyticsService | null,
    @Optional() private retailRocketCarouselStateService: RetailRocketCarouselStateService | null,
    private productListItemContextSource: ProductListItemContextSource,
    private customActiveCartService: CustomActiveCartService,
    private priceService: PriceService,
    private productBonusService: ProductBonusService,
    private selectedPriceService: SelectedPriceService,
    public transitPriceStateService: TransitPriceStateService,
    protected featureFlagService: FeatureFlagService,
    private productListReviewsService: ProductListReviewsService,
    private productWishlistService: ProductWishlistService,
    private comparisonService: ComparisonService,
    private deviceService: DeviceService,
    private bonusSystemFeatureAvailableService: BonusSystemFeatureAvailableService,
  ) {}

  ngOnInit(): void {
    this.prices$ = this.productListItemContextSource.product$.pipe(
      map((product) => product as CustomProduct),
      tap((product: CustomProduct) => {
        this.priceService.init(product);
      }),
      switchMap(({ code }) => this.selectedPriceService.getPricesBySelectedUnit(code)),
    );

    this.hasPersonalPrice$ = this.selectedPriceService.hasPersonalPrice(this.product.code);

    this.rating$ = this.productListReviewsService.getReviews(this.product.code).pipe(
      filter(Boolean),
      map((rating) => rating.score.toFixed(1)),
    );

    const isBonusFeatureAvailableForUser$ = this.bonusSystemFeatureAvailableService
      .isFeatureAvailableForUser()
      .pipe(map(Boolean), distinctUntilChanged());

    this.bonuses$ = combineLatest([
      this.productListItemContextSource.product$.pipe(map((product) => product as CustomProduct)),
      isBonusFeatureAvailableForUser$,
    ]).pipe(
      switchMap(([product, isFeatureAvailableForUser]) => {
        if (!isFeatureAvailableForUser) {
          return of([] as ProductBonus[]);
        }

        this.productBonusService.init(product);

        return this.productBonusService.getBonusesBySelectedUnit(product.code);
      }),
      map((bonuses: ProductBonus[]) => (bonuses[0]?.value > 0 ? bonuses[0] : null)),
    );

    this.transitInstance$ = this.productListItemContextSource.product$.pipe(
      map((product) => product as CustomProduct),
      switchMap((product: CustomProduct) => this.transitPriceStateService.getTransitState(product)),
    );

    this.isOutOfStockWithPriceFlagEnabled$ = this.featureFlagService
      .isEnabled('outOfStockWithPrice')
      .pipe(distinctUntilChanged());

    this.hasEntry$ = this.customActiveCartService
      .getEntry(this.product.code)
      .pipe(map((entry: OrderEntry | undefined) => Boolean(entry)));

    this.desktopWishlistVisible$ = this.productWishlistService.hasEntry(this.product.code).pipe(distinctUntilChanged());
    this.desktopComparisonVisible$ = this.comparisonService.hasEntry(this.product.code).pipe(distinctUntilChanged());
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.product) {
      this.productListItemContextSource.product$.next(this.product);
    }
  }

  onLinkClick(): void {
    this.productListAnalyticsService?.onClickProductInList(this.product, this.resolveProductIndex());
    this.retailRocketCarouselStateService?.clickToLink(this.product.code);
  }

  private resolveProductIndex(): number | undefined {
    if (this.productIndex !== undefined && this.productIndex !== null) {
      return this.productIndex;
    }

    return this.productListAnalyticsService?.getProductIndex(this.product.code);
  }

  onAddToCart(): void {
    this.retailRocketCarouselStateService?.clickToAddToCart(this.product.code);
  }
}
